import { Point2d } from "../../../common/geometry/Geom2d";
import { useEffect, useState } from "react";

export type TabletopCanvasMouseEvent = {
    position: Point2d;
};

export function useTabletopCanvasInputHandler(
    ref: React.RefObject<HTMLCanvasElement>,
    callbacks?: {
        onMouseMove?: (event: MouseEvent) => void;
        onMouseDrag?: (event: MouseEvent) => void;
        onMouseDown?: (event: MouseEvent) => void;
        onMouseUp?: (event: MouseEvent) => void;
        onMouseWheel?: (event: WheelEvent) => void;
        onKeyPress?: (event: KeyboardEvent) => void;
        onKeyDown?: (event: KeyboardEvent) => void;
        onKeyUp?: (event: KeyboardEvent) => void;
    },
) {
    const [isDragging, setIsDragging] = useState<boolean>(false);

    useEffect(() => {
        function handleMouseMove(event: MouseEvent) {
            event.preventDefault();
            callbacks?.onMouseMove?.(event);
        }

        function handleMouseDrag(event: MouseEvent) {
            if (isDragging) {
                event.preventDefault();
                callbacks?.onMouseDrag?.(event);
            }
        }

        function handleMouseDown(event: MouseEvent) {
            event.preventDefault();

            setIsDragging(true);

            // Remove existing listener, if it exists
            document.removeEventListener("mousemove", handleMouseDrag);

            document.addEventListener("mousemove", handleMouseDrag);

            callbacks?.onMouseDown?.(event);
        }

        function handleMouseUp(event: MouseEvent) {
            event.preventDefault();

            setIsDragging(false);

            document.removeEventListener("mousemove", handleMouseDrag);

            callbacks?.onMouseUp?.(event);
        }

        function handleMouseWheel(event: WheelEvent) {
            event.preventDefault();

            callbacks?.onMouseWheel?.(event);
        }

        function handleKeyPress(event: KeyboardEvent) {
            callbacks?.onKeyPress?.(event as KeyboardEvent);
        }

        function handleKeyDown(event: KeyboardEvent) {
            callbacks?.onKeyDown?.(event as KeyboardEvent);
        }

        function handleKeyUp(event: KeyboardEvent) {
            callbacks?.onKeyUp?.(event as KeyboardEvent);
        }

        function hideDefaultContextMenu(event: MouseEvent) {
            event.preventDefault();
        }

        ref.current?.addEventListener("pointermove", handleMouseMove);
        document.addEventListener("pointermove", handleMouseDrag);
        ref.current?.addEventListener("pointerdown", handleMouseDown);
        document.addEventListener("pointerup", handleMouseUp);
        ref.current?.addEventListener("wheel", handleMouseWheel);

        ref.current?.addEventListener("contextmenu", hideDefaultContextMenu);
        document.addEventListener("keypress", handleKeyPress);
        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("keyup", handleKeyUp);

        const refVal = ref.current;

        return () => {
            refVal?.removeEventListener("pointermove", handleMouseMove);
            document.removeEventListener("pointermove", handleMouseDrag);
            refVal?.removeEventListener("pointerdown", handleMouseDown);
            document.removeEventListener("pointerup", handleMouseUp);
            refVal?.removeEventListener("wheel", handleMouseWheel);
            refVal?.removeEventListener("contextmenu", hideDefaultContextMenu);
            document.removeEventListener("keypress", handleKeyPress);
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("keyup", handleKeyUp);
        };
    }, [
        ref,
        callbacks,
        callbacks?.onMouseDown,
        callbacks?.onMouseDrag,
        callbacks?.onMouseMove,
        callbacks?.onMouseUp,
        callbacks?.onMouseWheel,
        callbacks?.onKeyPress,
        callbacks?.onKeyDown,
        callbacks?.onKeyUp,
        isDragging,
    ]);
}
